import { toast } from "sonner";
import { API_ENDPOINTS, HTTP_METHODS } from "../config/api";
import { BaseApiService, ApiResponse, PaginationInfo } from "./baseApi";
import { Role } from './roleApi';
import { 
  User, 
  UserCreateRequest, 
  UserUpdateRequest, 
  UserProfile, 
  UserPreferences, 
  UserActivityLog, 
  ConsolidatedUserProfile,
  Menu,
  RoleMenu
} from '../types/user';

/**
 * USER UPDATE METHODS - CLEAR SEPARATION OF CONCERNS:
 * 
 * 1. updateCompleteUserProfile() - For User Profile Updates
 *    - Endpoint: /auth/users/{id}/profile
 *    - Use Case: User updates their own profile
 *    - Includes: User info + preferences (email, language, timezone, etc.)
 *    - Used in: UserProfilePage.tsx
 * 
 * 2. updateUserWithRolesAndBranches() - For User Management Updates
 *    - Endpoint: /auth/users/{id}/with-roles-and-branches
 *    - Use Case: Admin/Manager updates user information
 *    - Includes: User info + roles + branches (NO preferences) + operatedBy
 *    - Used in: UserForm.tsx (User Management)
 * 
 * These methods serve different purposes and should NOT be interchanged.
 */
// TODO: Replace with actual current user ID when JWT is implemented
const CURRENT_USER_ID = 9;

// Import shared types from types/user.ts

// Additional interfaces needed for user management
export interface UserRole {
  id: number;
  userId: number;
  roleId: number;
  role: Role;
  isActive: boolean;
}

export interface UserBranch {
  id: number;
  userId: number;
  branchId: number;
  branch: UserBranchInfo;
  isActive: boolean;
}

export interface UserBranchInfo {
  id: number;
  branchName: string;
  branchCode: string;
  address?: string;
  isActive: boolean;
}





class UserApiService extends BaseApiService {

  // Get all users with pagination and branch filtering
  async getUsers(page: number = 1, pageSize: number = 10, branchId?: number): Promise<ApiResponse<User[]>> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      if (branchId) {
        params.append('branchId', branchId.toString());
      }

      const finalUrl = `${API_ENDPOINTS.USERS.LIST}?${params.toString()}`;
      // console.log('🌐 API call to:', finalUrl);
      
        const response = await this.makeRequest<ApiResponse<User[]>>(finalUrl);
      return response;
    } catch (error) {
      toast.error('Failed to fetch users');
      throw error;
    }
  }

  // Get user by ID
  async getUserById(id: string): Promise<ApiResponse<User>> {
    try {
      const response = await this.makeRequest<ApiResponse<User>>(API_ENDPOINTS.USERS.DETAIL(id));
      return response;
    } catch (error) {
      toast.error('Failed to fetch user details');
      throw error;
    }
  }

  // Get user profile with roles, branches, and preferences
  async getUserProfile(id: string): Promise<ApiResponse<UserProfile>> {
    try {
      const response = await this.makeRequest<ApiResponse<UserProfile>>(API_ENDPOINTS.USERS.PROFILE);
      return response;
    } catch (error) {
      toast.error('Failed to fetch user profile');
      throw error;
    }
  }

  // Create new user
  async createUser(userData: UserCreateRequest, branchId: number, createdBy: number): Promise<ApiResponse<User>> {
    try {
      const params = new URLSearchParams({
        branchId: branchId.toString(),
        createdBy: createdBy.toString(),
      });
      
      const url = `${API_ENDPOINTS.USERS.CREATE}?${params.toString()}`;
      const response = await this.makeRequest<ApiResponse<User>>(url, {
        method: HTTP_METHODS.POST,
        body: JSON.stringify(userData),
      });
      toast.success('User created successfully!');
      return response;
    } catch (error) {
      toast.error('Failed to create user');
      throw error;
    }
  }

  // Update user
  async updateUser(id: string, userData: UserUpdateRequest, branchId: number, updatedBy: number): Promise<ApiResponse<User>> {
    try {
      const params = new URLSearchParams({
        branchId: branchId.toString(),
        updatedBy: updatedBy.toString(),
      });
      
      const url = `${API_ENDPOINTS.USERS.UPDATE(id)}?${params.toString()}`;
      const response = await this.makeRequest<ApiResponse<User>>(url, {
        method: HTTP_METHODS.PUT,
        body: JSON.stringify(userData),
      });
      toast.success('User updated successfully!');
      return response;
    } catch (error) {
      toast.error('Failed to update user');
      throw error;
    }
  }

  /**
   * Update complete user profile (user info + preferences) in a single request
   * 
   * USE CASE: When a user updates their own profile from UserProfilePage
   * ENDPOINT: /auth/users/{id}/profile
   * INCLUDES: User info + preferences (email notifications, language, timezone, etc.)
   * 
   * @param userId - The ID of the user whose profile is being updated
   * @param profileData - Complete profile data including preferences
   * @param updatedBy - ID of the user making the update (usually the same as userId)
   */
  async updateCompleteUserProfile(
    userId: number, 
    profileData: Partial<UserUpdateRequest & { preferences?: UserPreferences }>, 
    updatedBy: number
  ): Promise<ApiResponse<any>> {
    try {
      const params = new URLSearchParams({
        updatedBy: updatedBy.toString(),
      });
      
      const url = `${API_ENDPOINTS.USERS.UPDATE_PROFILE(userId.toString())}?${params.toString()}`;
      
      // console.log(`Updating user profile for ID ${userId}:`, profileData);
      
      const response = await this.makeRequest<ApiResponse<any>>(url, {
        method: HTTP_METHODS.PUT,
        body: JSON.stringify(profileData),
      });
      
      // console.log('Profile update response:', response);
      toast.success('Profile updated successfully!');
      return response;
    } catch (error) {
      // console.error('Profile update error:', error);
      toast.error('Failed to update profile');
      throw error;
    }
  }

  /**
   * Update user with roles and branches in a single request (for User Management)
   * 
   * USE CASE: When an admin/manager updates user information from UserForm/UserManagement
   * ENDPOINT: /auth/users/{id}/with-roles-and-branches
   * INCLUDES: User info + roles + branches (NO preferences) + operatedBy
   * 
   * @param userId - The ID of the user being updated
   * @param userData - User data including roles and branches (no preferences)
   * @param operatedBy - ID of the user/admin performing the operation
   */
  async updateUserWithRolesAndBranches(
    userId: number, 
    userData: Partial<UserUpdateRequest & { roles?: any[], branches?: any[] }>,
    operatedBy: number
  ): Promise<ApiResponse<any>> {
    try {
      const url = API_ENDPOINTS.USERS.WITH_ROLES_AND_BRANCHES(userId.toString());
      
      // Add operatedBy to the payload
      const payloadWithOperatedBy = {
        ...userData,
        operatedBy
      };
      
      // console.log(`Updating user with roles and branches for ID ${userId}:`, payloadWithOperatedBy);
      
      const response = await this.makeRequest<ApiResponse<any>>(url, {
        method: HTTP_METHODS.PUT,
        body: JSON.stringify(payloadWithOperatedBy),
      });
      
      // console.log('User update with roles and branches response:', response);
      toast.success('User updated successfully!');
      return response;
    } catch (error) {
      // console.error('User update with roles and branches error:', error);
      toast.error('Failed to update user');
      throw error;
    }
  }

  // Delete user
  async deleteUser(id: string): Promise<ApiResponse<void>> {
    try {
      const response = await this.makeRequest<ApiResponse<void>>(API_ENDPOINTS.USERS.DELETE(id), {
        method: HTTP_METHODS.DELETE,
      });
      toast.success('User deleted successfully!');
      return response;
    } catch (error) {
      toast.error('Failed to delete user');
      throw error;
    }
  }

  // Change password
  async changePassword(userId: number, oldPassword: string, newPassword: string): Promise<ApiResponse<void>> {
    try {
      const params = new URLSearchParams({
        oldPassword,
        newPassword,
      });
      const url = `${API_ENDPOINTS.USERS.CHANGE_PASSWORD(userId.toString())}?${params.toString()}`;
      const response = await this.makeRequest<ApiResponse<void>>(url, {
        method: HTTP_METHODS.POST,
      });
      toast.success('Password changed successfully!');
      return response;
    } catch (error) {
      toast.error('Failed to change password');
      throw error;
    }
  }

  // Get user activity logs
  async getUserActivityLogs(userId: string, page: number = 1, pageSize: number = 20): Promise<ApiResponse<UserActivityLog[]>> {
    try {
      const params = new URLSearchParams({
        userId,
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      const response = await this.makeRequest<ApiResponse<UserActivityLog[]>>(`/users/${userId}/activity-logs?${params.toString()}`);
      return response;
    } catch (error) {
      toast.error('Failed to fetch activity logs');
      throw error;
    }
  }

  // Get all roles
  async getRoles(): Promise<ApiResponse<Role[]>> {
    try {
      const response = await this.makeRequest<ApiResponse<Role[]>>('/auth/roles');
      return response;
    } catch (error) {
      toast.error('Failed to fetch roles');
      throw error;
    }
  }

  // Get all menus
  async getMenus(): Promise<ApiResponse<Menu[]>> {
    try {
      const response = await this.makeRequest<ApiResponse<Menu[]>>('/auth/menus');
      return response;
    } catch (error) {
      toast.error('Failed to fetch menus');
      throw error;
    }
  }

  // Assign role to user
  async assignUserRole(userRoleData: { userId: number; roleId: number }): Promise<ApiResponse<UserRole>> {
    try {
      const response = await this.makeRequest<ApiResponse<UserRole>>('/auth/user-roles', {
        method: HTTP_METHODS.POST,
        body: JSON.stringify(userRoleData),
      });
      toast.success('Role assigned successfully!');
      return response;
    } catch (error) {
      toast.error('Failed to assign role');
      throw error;
    }
  }

  // Remove role from user
  async removeUserRole(userRoleId: number): Promise<ApiResponse<void>> {
    try {
      const response = await this.makeRequest<ApiResponse<void>>(`/auth/user-roles/${userRoleId}`, {
        method: HTTP_METHODS.DELETE,
      });
      toast.success('Role removed successfully!');
      return response;
    } catch (error) {
      toast.error('Failed to remove role');
      throw error;
    }
  }

  // Assign branch to user
  async assignUserBranch(userBranchData: { userId: number; branchId: number }): Promise<ApiResponse<UserBranch>> {
    try {
      const response = await this.makeRequest<ApiResponse<UserBranch>>('/auth/user-branches', {
        method: HTTP_METHODS.POST,
        body: JSON.stringify(userBranchData),
      });
      toast.success('Branch assigned successfully!');
      return response;
    } catch (error) {
      toast.error('Failed to assign branch');
      throw error;
    }
  }

  // Remove branch from user
  async removeUserBranch(userBranchId: number): Promise<ApiResponse<void>> {
    try {
      const response = await this.makeRequest<ApiResponse<void>>(`/auth/user-branches/${userBranchId}`, {
        method: HTTP_METHODS.DELETE,
      });
      toast.success('Branch removed successfully!');
      return response;
    } catch (error) {
      toast.error('Failed to remove branch');
      throw error;
    }
  }

  // Update user preferences
  async updateUserPreferences(preferencesData: Partial<UserPreferences>): Promise<ApiResponse<UserPreferences>> {
    try {
      const response = await this.makeRequest<ApiResponse<UserPreferences>>('/auth/user-preferences', {
        method: HTTP_METHODS.PUT,
        body: JSON.stringify(preferencesData),
      });
      toast.success('Preferences updated successfully!');
      return response;
    } catch (error) {
      toast.error('Failed to update preferences');
      throw error;
    }
  }

  // Search users with branch filtering
  async searchUsers(query: string, page: number = 1, pageSize: number = 10, branchId?: number): Promise<ApiResponse<User[]>> {
    try {
      const params = new URLSearchParams({
        keyword: query,
      });

      if (branchId) {
        params.append('branchId', branchId.toString());
      }

      const response = await this.makeRequest<ApiResponse<User[]>>(`/auth/users/search?${params.toString()}`);
      return response;
    } catch (error) {
      toast.error('Failed to search users');
      throw error;
    }
  }

  // Assign multiple branches to user
  async assignMultipleBranchesToUser(userId: number, branchIds: number[], assignedBy: number): Promise<ApiResponse<void>> {
    try {
      const params = new URLSearchParams({
        assignedBy: assignedBy.toString(),
      });
      
      const url = `/auth/user-branches/user/${userId}/assign-multiple?${params.toString()}`;
      const response = await this.makeRequest<ApiResponse<void>>(url, {
        method: HTTP_METHODS.POST,
        body: JSON.stringify(branchIds),
      });
      toast.success('Branches assigned successfully!');
      return response;
    } catch (error) {
      toast.error('Failed to assign branches');
      throw error;
    }
  }

  // Assign multiple roles to user
  async assignMultipleRolesToUser(userId: number, roleIds: number[], assignedBy: number): Promise<ApiResponse<void>> {
    try {
      const params = new URLSearchParams({
        assignedBy: assignedBy.toString(),
      });
      
      const url = `/auth/user-roles/user/${userId}/assign-multiple?${params.toString()}`;
      const response = await this.makeRequest<ApiResponse<void>>(url, {
        method: HTTP_METHODS.POST,
        body: JSON.stringify(roleIds),
      });
      toast.success('Roles assigned successfully!');
      return response;
    } catch (error) {
      toast.error('Failed to assign roles');
      throw error;
    }
  }

  // Get user roles by user ID
  async getUserRolesByUserId(userId: number, activeOnly: boolean = true): Promise<ApiResponse<UserRole[]>> {
    try {
      const params = new URLSearchParams({
        activeOnly: activeOnly.toString(),
      });
      
      const url = `/auth/user-roles/user/${userId}?${params.toString()}`;
      const response = await this.makeRequest<ApiResponse<UserRole[]>>(url);
      return response;
    } catch (error) {
      toast.error('Failed to fetch user roles');
      throw error;
    }
  }

  // Get user branches by user ID
  async getUserBranchesByUserId(userId: number, activeOnly: boolean = true): Promise<ApiResponse<UserBranch[]>> {
    try {
      const params = new URLSearchParams({
        activeOnly: activeOnly.toString(),
      });
      
      const url = `/auth/user-branches/user/${userId}?${params.toString()}`;
      const response = await this.makeRequest<ApiResponse<UserBranch[]>>(url);
      return response;
    } catch (error) {
      toast.error('Failed to fetch user branches');
      throw error;
    }
  }

  // Get current user profile (assuming user ID = 9 for now)
  async getCurrentUserProfile(): Promise<ApiResponse<User>> {
    try {
      // TODO: Replace with actual current user ID when JWT is implemented
      // For now, hardcoded to user ID = 9
      // Later: const currentUserId = getCurrentUserIdFromJWT() or from context
      const response = await this.getUserById(CURRENT_USER_ID.toString());
      return response;
    } catch (error) {
      toast.error('Failed to fetch current user profile');
      throw error;
    }
  }

  // Get consolidated user profile with all related data
  async getConsolidatedUserProfile(userId: number): Promise<ApiResponse<ConsolidatedUserProfile>> {
    try {
      const response = await this.makeRequest<ApiResponse<ConsolidatedUserProfile>>(
        API_ENDPOINTS.USERS.PROFILE_BY_ID(userId.toString())
      );
      return response;
    } catch (error) {
      toast.error('Failed to fetch consolidated user profile');
      throw error;
    }
  }

  // Get user preferences by user ID
  async getUserPreferencesByUserId(userId: number): Promise<ApiResponse<UserPreferences>> {
    try {
      const response = await this.makeRequest<ApiResponse<UserPreferences>>(`/auth/user-preferences/user/${userId}`);
      return response;
    } catch (error) {
      toast.error('Failed to fetch user preferences');
      throw error;
    }
  }
}

export const userApiService = new UserApiService();
